import { Alert, Button, Card, CardBody, Spinner } from '@foundation/ui'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { AppLayout } from '@/components/AppLayout'
import { api, type StripeStatus } from '@/lib/api'
import { isAuthenticated } from '@/lib/auth-store'
import { toast } from '@/lib/toast'

export const Route = createFileRoute('/settings-stripe')({
  beforeLoad: () => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login' })
    }
  },
  component: StripeSettingsPage,
})

function StripeSettingsPage() {
  const [status, setStatus] = useState<StripeStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)

  useEffect(() => {
    api
      .getStripeStatus()
      .then((res) => setStatus(res.data))
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Failed to load Stripe status')
      })
      .finally(() => setIsLoading(false))
  }, [])

  const handleConnect = () => {
    setIsConnecting(true)
    api
      .getStripeConnectUrl()
      .then((res) => {
        window.location.href = res.data.redirect_url
      })
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Failed to start Stripe connection')
        setIsConnecting(false)
      })
  }

  const handleDisconnect = () => {
    if (
      !confirm(
        'Are you sure you want to disconnect Stripe? This will stop processing cancellations.'
      )
    )
      return
    setIsDisconnecting(true)
    api
      .disconnectStripe()
      .then(() => {
        setStatus((prev) => (prev ? { ...prev, connected: false, stripe_account_id: null } : null))
        toast.success('Stripe disconnected successfully')
      })
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Failed to disconnect Stripe')
      })
      .finally(() => setIsDisconnecting(false))
  }

  return (
    <AppLayout title="Stripe Connection">
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <Spinner size="lg" />
        </div>
      ) : (
        <>
          <Card style={{ marginBottom: '1.5rem' }}>
            <CardBody>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                Stripe Connection
              </h2>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                Connect your Stripe account to automatically detect subscription cancellations and
                trigger win-back sequences.
              </p>

              {status?.connected ? (
                <>
                  <Alert variant="success" style={{ marginBottom: '1rem' }}>
                    Connected to Stripe account: <strong>{status.stripe_account_id}</strong>
                    {status.livemode ? ' (Live mode)' : ' (Test mode)'}
                  </Alert>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      <p>
                        Status: <strong>{status.status}</strong>
                      </p>
                      {status.connected_at && (
                        <p>Connected: {new Date(status.connected_at).toLocaleDateString()}</p>
                      )}
                      {status.last_webhook_at && (
                        <p>Last webhook: {new Date(status.last_webhook_at).toLocaleString()}</p>
                      )}
                    </div>
                  </div>
                  <div
                    style={{
                      marginTop: '1.5rem',
                      paddingTop: '1rem',
                      borderTop: '1px solid var(--border)',
                    }}
                  >
                    <Button variant="danger" onClick={handleDisconnect} loading={isDisconnecting}>
                      Disconnect Stripe
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Alert variant="info" style={{ marginBottom: '1rem' }}>
                    Stripe is not connected. Connect your account to start detecting cancellations
                    automatically.
                  </Alert>
                  <Button variant="primary" onClick={handleConnect} loading={isConnecting}>
                    Connect Stripe Account
                  </Button>
                </>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </AppLayout>
  )
}
